import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calculator, ChatCircleText, MapPin, PlusCircle } from "@phosphor-icons/react";

/* ═══════════════ SECTION 3 · What Do You Want To Do? ═══════════════ */
const GOALS = [
  { emoji: "🇨🇦", title: "Start a Business in Canada", desc: "Company formation and business presence for residents and non-residents.", cta: "Explore Canada", href: "/countries/canada" },
  { emoji: "🌎", title: "Open a Company Overseas", desc: "Explore company formation across OBA-supported countries.", cta: "Explore Countries", href: "/countries" },
  { emoji: "🚀", title: "Expand My Existing Business", desc: "Enter Canada or another international market.", cta: "Expansion Options", href: "/services" },
  { emoji: "📦", title: "Sell Products Internationally", desc: "Explore new markets, distributors and business opportunities.", cta: "Explore Trade Support", href: "/contact?goal=export" },
  { emoji: "🤝", title: "Find Buyers, Partners or Distributors", desc: "Business development and international connections.", cta: "Business Development", href: "/contact?goal=partners" },
  { emoji: "📍", title: "Get a Business Address", desc: "Virtual office and professional business presence.", cta: "Virtual Office Solutions", href: "/services" },
  { emoji: "🏦", title: "Banking & Business Support", desc: "Explore corporate banking assistance and operational support.", cta: "Explore Support", href: "/contact?goal=banking" },
  { emoji: "🛒", title: "Start or Expand an Online Business", desc: "Support for e-commerce sellers, consultants, creators and digital businesses.", cta: "Explore Options", href: "/contact?goal=online-business" },
];

export function HomeGoals() {
  return (
    <section className="py-20 bg-gos-bone border-b border-gos-border" data-testid="home-goals">
      <div className="container-gos">
        <div className="max-w-3xl mb-10">
          <div className="overline mb-3">Get started</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">What Do You Want To Do?</h2>
          <p className="text-gos-slate mt-3">Choose your goal. We&apos;ll show you where to start.</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {GOALS.map((g) => (
            <Link
              key={g.title} to={g.href}
              data-testid={`goal-${g.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="bg-white border border-gos-border rounded-sm p-6 flex flex-col h-full card-lift group"
            >
              <div className="text-3xl mb-4">{g.emoji}</div>
              <h3 className="font-display text-base leading-tight text-gos-navy">{g.title}</h3>
              <p className="text-xs text-gos-slate mt-2 leading-relaxed flex-grow">{g.desc}</p>
              <div className="mt-4 pt-4 border-t border-gos-border flex items-center gap-1.5 text-xs font-semibold text-gos-navy group-hover:text-gos-maple transition-colors">
                {g.cta} <ArrowRight size={12} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════ SECTION 4 · One Setup. Bigger Possibilities. ═══════════════ */
const BENEFITS = [
  { title: "Build International Credibility", desc: "Establish a professional presence in major global markets.", href: "/services" },
  { title: "Reach New Customers", desc: "Explore opportunities beyond your home market.", href: "/countries" },
  { title: "Sell Across Borders", desc: "Support for e-commerce, exporters and product businesses.", href: "/contact?goal=export" },
  { title: "Access Local Support", desc: "Use OBA's growing international partner network.", href: "/#global-partners-section" },
  { title: "Expand When You're Ready", desc: "Start in one market and explore additional countries as your business grows.", href: "/compare" },
];

export function HomeBenefits() {
  return (
    <section className="py-20 bg-white border-b border-gos-border" data-testid="home-benefits">
      <div className="container-gos">
        <div className="max-w-3xl mb-10">
          <div className="overline mb-3">Why OBA</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">One Setup. Bigger Possibilities.</h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {BENEFITS.map((b, i) => (
            <Link
              key={b.title} to={b.href}
              data-testid={`benefit-${i}`}
              className="bg-gos-bone border border-gos-border rounded-sm p-5 flex flex-col h-full card-lift group"
            >
              <div className="font-display text-base text-gos-navy leading-tight">{b.title}</div>
              <p className="text-xs text-gos-slate mt-2 leading-relaxed flex-grow">{b.desc}</p>
              <div className="mt-4 pt-3 border-t border-gos-border text-xs font-semibold text-gos-navy group-hover:text-gos-maple transition-colors flex items-center gap-1">
                Learn more <ArrowRight size={11} weight="bold" />
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ═══════════════ SECTION 5 · Who Is OBA For? ═══════════════ */
const CATEGORIES = [
  {
    key: "online",
    label: "Online Business",
    items: ["Amazon Sellers", "Shopify Sellers", "YouTube Creators", "Content Creators", "Freelancers", "Affiliate Marketers", "Online Consultants", "Online Course Businesses", "Digital Marketing Agencies", "App Developers", "Website Businesses", "E-commerce Sellers"],
  },
  {
    key: "tech",
    label: "Technology",
    items: ["IT Companies", "Software Companies", "SaaS Businesses", "AI Startups", "Technology Consultants", "App Businesses", "Digital Startups"],
  },
  {
    key: "trade",
    label: "Products & Trade",
    items: ["Manufacturers", "Exporters", "Importers", "Wholesalers", "Distributors", "Private Label Brands", "Food & Spice Exporters", "Agricultural Products", "Textiles & Garments", "Jewellery", "Handicrafts", "Cosmetics & Beauty Brands", "Tea & Beverage Businesses"],
  },
  {
    key: "professional",
    label: "Professional & Service Businesses",
    items: ["Business Consultants", "Training Companies", "Education Businesses", "Recruitment Businesses", "Coaches", "Professional Services", "Marketing Agencies", "Business Service Providers"],
  },
  {
    key: "established",
    label: "Established Businesses",
    items: ["SMEs", "Family Businesses", "Retail Businesses", "Restaurant Brands", "Hospitality Businesses", "Construction Companies", "Real Estate Businesses", "Automotive Businesses", "Franchise Brands", "Industrial Businesses"],
  },
];

export function HomeWhoFor() {
  const [openCat, setOpenCat] = useState(null); // mobile accordion

  return (
    <section className="py-20 bg-gos-bone border-b border-gos-border" data-testid="home-who-for">
      <div className="container-gos">
        <div className="max-w-3xl mb-10">
          <div className="overline mb-3">Who OBA is for</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
            You Don&apos;t Have to Be a Big Company <span className="italic text-gos-navy">to Go Global.</span>
          </h2>
          <p className="text-gos-slate mt-4 leading-relaxed">
            From one-person online businesses to established manufacturers, OBA helps
            businesses explore international opportunities.
          </p>
        </div>

        <div className="space-y-6">
          {CATEGORIES.map((cat) => (
            <div key={cat.key} data-testid={`whofor-${cat.key}`} className="bg-white border border-gos-border rounded-sm">
              {/* Desktop: always-open header */}
              <div className="hidden md:block px-6 pt-5 pb-2">
                <div className="overline text-gos-navy">{cat.label}</div>
              </div>
              {/* Mobile: clickable accordion header */}
              <button
                type="button"
                onClick={() => setOpenCat(openCat === cat.key ? null : cat.key)}
                className="md:hidden w-full flex items-center justify-between px-5 py-4"
                data-testid={`whofor-toggle-${cat.key}`}
              >
                <span className="overline text-gos-navy">{cat.label}</span>
                <PlusCircle size={18} weight="duotone" className={`text-gos-sand transition-transform ${openCat === cat.key ? "rotate-45" : ""}`} />
              </button>
              {/* Tags */}
              <div className={`px-5 md:px-6 pb-5 md:pb-6 md:pt-0 flex flex-wrap gap-2 ${openCat === cat.key ? "block" : "hidden md:flex"}`}>
                {cat.items.map((tag) => (
                  <Link
                    key={tag}
                    to={`/contact?business_type=${encodeURIComponent(tag)}`}
                    className="text-xs px-3 py-1.5 rounded-sm border border-gos-border bg-gos-bone text-gos-charcoal hover:bg-gos-navy hover:text-white hover:border-gos-navy transition-colors"
                  >
                    {tag}
                  </Link>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-10 bg-white border border-gos-border rounded-sm p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <div className="font-display text-lg text-gos-navy">Don&apos;t see your business?</div>
            <p className="text-sm text-gos-slate mt-1 max-w-lg">
              If you want to start, sell, source, operate or expand internationally, talk to us.
            </p>
          </div>
          <Link to="/contact?goal=custom" className="btn-primary" data-testid="whofor-tell-us-btn">
            Tell Us About Your Business <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════ SECTION 6 · Going Global Can Be Simple (3-step) ═══════════════ */
const STEPS = [
  { n: 1, title: "Tell Us Your Goal", desc: "Country, business and what you want to achieve." },
  { n: 2, title: "See Your Options & Cost", desc: "Compare markets and calculate estimated setup costs." },
  { n: 3, title: "Start Your Setup", desc: "OBA coordinates the process with its local professional network." },
];

export function HomeSimpleProcess() {
  return (
    <section className="py-20 bg-white border-b border-gos-border" data-testid="home-simple-process">
      <div className="container-gos">
        <div className="max-w-3xl mb-10">
          <div className="overline mb-3">How it works</div>
          <h2 className="font-display text-3xl sm:text-4xl text-gos-charcoal tracking-tight">
            Going Global <span className="italic text-gos-navy">Can Be Simple.</span>
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 relative">
          {STEPS.map((s, i) => (
            <div key={s.n} data-testid={`step-${s.n}`} className="bg-gos-bone border border-gos-border rounded-sm p-6 relative">
              <div className="font-display text-4xl text-gos-maple leading-none">{s.n}.</div>
              <h3 className="font-display text-lg text-gos-navy mt-3">{s.title}</h3>
              <p className="text-sm text-gos-slate mt-2 leading-relaxed">{s.desc}</p>
              {i < STEPS.length - 1 && (
                <ArrowRight size={20} weight="bold" className="text-gos-sand hidden md:block absolute -right-3 top-1/2 -translate-y-1/2" />
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 flex flex-wrap gap-3">
          <Link to="/calculator" className="btn-primary" data-testid="simple-process-calc"><Calculator size={16} weight="bold" /> Calculate Setup Cost</Link>
          <Link to="/contact" className="btn-sand" data-testid="simple-process-book"><ChatCircleText size={16} weight="bold" /> Book Free Consultation</Link>
        </div>
      </div>
    </section>
  );
}

/* ═══════════════ SECTION 7 · Canada CTA (compact) ═══════════════ */
export function HomeCanadaCTA() {
  return (
    <section className="py-16 bg-gos-bone border-b border-gos-border" data-testid="home-canada-cta">
      <div className="container-gos">
        <div
          className="relative overflow-hidden bg-white border border-gos-maple/40 rounded-sm p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6"
        >
          <div className="absolute inset-0 pointer-events-none opacity-40" style={{ background: "radial-gradient(circle at 100% 0%, rgba(239,62,66,0.12), transparent 60%)" }} />
          <div className="relative">
            <div className="text-4xl mb-3">🇨🇦</div>
            <h2 className="font-display text-2xl md:text-3xl text-gos-charcoal tracking-tight">
              Thinking About Canada?
            </h2>
            <p className="text-sm text-gos-slate mt-3 leading-relaxed max-w-2xl">
              Whether you&apos;re an Amazon seller, consultant, IT company, exporter,
              manufacturer or established overseas business, explore what a Canadian
              business presence could offer.
            </p>
          </div>
          <div className="relative flex flex-col gap-2 md:min-w-[240px]">
            <Link to="/countries/canada" className="btn-primary justify-center" data-testid="canada-cta-explore">
              <MapPin size={14} weight="bold" /> Explore Canada
            </Link>
            <Link to="/calculator?country=canada" className="btn-secondary justify-center" data-testid="canada-cta-calc">
              <Calculator size={14} weight="bold" /> Calculate Canada Setup Cost
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
